<template>
  <v-list
    key="files-list"
    group
    tag="v-list"
    flat
    dense
    class="py-0 d-flex flex-column file-list"
    hide-on-leave
    leave-absolute
    :class="{ 'drag-active': isDragging }"
    @dragenter.prevent="dragenter"
    @dragleave.prevent="dragleave"
    @dragover.prevent
    @drop.prevent="handleDrop"
  >
    <v-list-item
      v-if="isDragging"
      key="dragging"
    >
      <v-list-item-content
        class="d-flex justify-center align-center flex-column"
      >
        <div class="d-flex justify-start mt-2 text--disabled">
          <v-icon large>
            mdi-cloud-upload
          </v-icon>
        </div>
        <span
          class="mt-1 mb-3 text-body-2 text-md-body-1 font-weight-normal text--secondary"
        >
          Release to Upload File
        </span>
      </v-list-item-content>
    </v-list-item>
    <v-list-item
      v-else-if="uploadProgress > 0"
      key="uploading"
    >
      <v-list-item-content>
        <div class="d-flex justify-center align-center flex-column">
          <div class="d-flex justify-start mt-2 text--disabled">
            <v-icon large>
              mdi-cloud-upload
            </v-icon>
          </div>
          <span
            class="mt-1 mb-3 d-flex align-center text-body-2 text-md-body-1 font-weight-normal text--secondary"
            style="gap: 8px"
          >
            <v-progress-circular
              :value="uploadProgress"
              color="review"
              size="15"
              width="3"
            />
            {{ Math.round(uploadProgress) }}% Uploaded
          </span>
        </div>
      </v-list-item-content>
    </v-list-item>
    <v-skeleton-loader
      v-else-if="parentLoading || loading"
      key="skeleton"
      width="100%"
      height="100%"
      type="list-item-avatar-two-line@5"
    />
    <v-list-item
      v-else-if="files.length === 0"
      key="nofiles"
      class="rounded-lg px-1"
    >
      <v-list-item-content>
        <v-list-item-subtitle
          class="text--disabled d-flex justify-center text-caption font-weight-bold"
        >
          No files found.
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
    <div
      v-for="(file, index) in files"
      :key="`file-${index}`"
    >
      <v-list-item
        class="rounded-lg px-2"
        :ripple="false"
        three-line
        style="opacity: 1 !important"
      >
        <v-list-item-content>
          <div
            class="d-flex justify-space-between"
            style="gap: 6px; width: 100%"
          >
            <div
              class="d-flex align-center justify-start"
              style="gap: 16px; flex: 1; min-width: 0"
            >
              <div class="d-flex shrink">
                <v-avatar
                  v-if="file.type === 'upload'"
                  tile
                  class="rounded-lg"
                  :color="
                    $vuetify.theme.isDark
                      ? 'background lighten-1'
                      : 'background'
                  "
                  :style="{
                    border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
                  }"
                >
                  <v-icon size="25">
                    mdi-file-document
                  </v-icon>
                </v-avatar>
                <v-avatar
                  v-else
                  tile
                  class="rounded-lg"
                  :color="
                    $vuetify.theme.isDark
                      ? 'background lighten-1'
                      : 'background'
                  "
                  :style="{
                    border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
                  }"
                >
                  <v-icon color="iconColor">
                    mdi-link-variant
                  </v-icon>
                </v-avatar>
              </div>
              <span
                class="d-flex flex-column"
                style="min-width: 0; width: 100%"
              >
                <div
                  class="align-start justify-start align-self-start flex-column d-flex text-body-2"
                  style="gap: 4px; width: 100%"
                >
                  <span
                    class="font-weight-bold text-body-2"
                    style="
                      max-height: 100px;
                      overflow-y: scroll;
                      scrollbar-width: none;
                    "
                  >
                    {{ file.file ?? file.notes }}
                  </span>
                  <span
                    v-if="file.notes?.length && file.notes !== file.file"
                    class="text-caption text--secondary font-weight-bold"
                    style="
                      line-height: 0.9rem !important;
                      max-height: 100px;
                      overflow-y: scroll;
                      scrollbar-width: none;
                    "
                  >
                    {{ `"${file.notes}"` }}
                  </span>
                  <div
                    class="d-flex align-center justify-start"
                    style="gap: 6px; width: 100%; overflow: hidden"
                  >
                    <v-icon
                      small
                      class="flex-shrink-0"
                    >mdi-account-outline</v-icon>
                    <span
                      class="text-caption font-weight-medium text--secondary text-truncate"
                      style="
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                      "
                      :class="
                        !file.createdBy?.length
                          ? 'text--disabled font-weight-normal'
                          : ''
                      "
                    >
                      {{
                        file.createdBy?.length
                          ? file.createdBy
                          : "-" | capitalize
                      }}</span>
                  </div>
                  <div
                    class="d-flex align-start justify-start"
                    style="gap: 6px"
                  >
                    <v-icon small>mdi-timeline-clock-outline</v-icon>
                    <span
                      class="text-caption text--secondary"
                      :class="
                        !file.created ? 'text--disabled font-weight-normal' : ''
                      "
                      style="line-height: 0.9rem !important"
                    >{{
                      $options.filters.moment(
                        file.created,
                        "M/DD/YY @ h:mm A"
                      )
                    }}</span>
                  </div>
                </div>
              </span>
            </div>
            <div
              class="d-flex justify-end align-center flex-shrink-0"
              style="gap: 6px"
            >
              <v-menu
                key="contactMenu"
                left
                bottom
                offset-y
                :nudge-bottom="8"
                content-class="rounded-lg"
                transition="slide-y-transition"
              >
                <template #activator="{ on, attrs, value }">
                  <v-btn
                    color="white"
                    class="text-caption review--text font-weight-bold rounded-lg"
                    small
                    text
                    icon
                    outlined
                    v-bind="attrs"
                    :loading="file.delete || file.download"
                    v-on="on"
                  >
                    <v-icon
                      small
                      color="review"
                    >
                      mdi-dots-vertical
                    </v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    key="email"
                    :href="file.url ?? file.path"
                    target="_blank"
                    dense
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon
                        small
                        v-text="'mdi-open-in-new'"
                      />
                    </v-list-item-icon>
                    <v-list-item-title>Open in New Tab</v-list-item-title>
                  </v-list-item>
                  <!-- <v-list-item dense key="call">
                    <v-list-item-icon class="mr-2">
                      <v-icon small v-text="'mdi-download'" />
                    </v-list-item-icon>
                    <v-list-item-title>Download</v-list-item-title>
                  </v-list-item> -->
                  <v-list-item
                    v-if="showFeatureOption && file.type === 'upload'"
                    key="feature"
                    dense
                    @click="$emit('feature', file)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon
                        small
                        color="primary"
                        v-text="'mdi-star-circle'"
                      />
                    </v-list-item-icon>
                    <v-list-item-title class="primary--text">
                      Feature Image
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    key="delete"
                    dense
                    @click="deleteFile(file)"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon
                        small
                        color="error"
                        v-text="'mdi-trash-can'"
                      />
                    </v-list-item-icon>
                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </v-list-item-content>
      </v-list-item>
      <v-divider
        v-if="index !== files.length - 1"
        style="width: 100%"
      />
    </div>
  </v-list>
</template>

<script>
// Libraries
import { mapGetters } from "vuex";
import {
  // Firestore
  firestore,
  collection,
  getDocs,
  where,
  query,
  doc,
  addDoc,
  writeBatch,
  // Storage
  storage,
  ref,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
} from "@/firebase/init";
import moment from "moment";
// Components

export default {
  name: "FilesList",
  props: {
    account: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    parentLoading: {
      default: false,
      type: Boolean,
      required: false,
    },
    relatedItem: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    showFeatureOption: {
      default: false,
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    loading: false,
    files: [],
    isDragging: false,
    dragCounter: 0,
    uploadProgress: 0,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedFileTypes: [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
  }),
  watch: {
    account: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length) {
          this.getFiles();
        }
      },
      deep: true,
    },
  },
  created() {
    this.setup();
  },
  methods: {
    /* API Calls */
    // GET
    async getFiles() {
      this.loading = true;
      this.files = [];

      if (!this.relatedItem?.id) {
        this.loading = false;
        return;
      }

      const files = collection(firestore, "media");
      const account = where("relatedID", "==", this.relatedItem.id);

      const q = query(files, account);
      const response = await getDocs(q);

      if (!response.empty) {
        let filePromises = response.docs
          .filter((item) => item.data().title !== "customer")
          .filter((item) => !item.data().path?.startsWith("onboarding/"))
          .map(async (file) => {
            let fileData = {
              id: file.id,
              created: file.data().created?.seconds
                ? moment(file.data().created.seconds * 1000).toDate()
                : moment(file.data().created).toDate(),
              file: file.data().file,
              type: file.data().type,
              path: file.data().path,
              notes: file.data().notes,
              createdBy: `${file.data().user.name.first} ${
                file.data().user.name.last
              }`,
              delete: false,
              download: false,
            };

            if (file.data().type === "upload") {
              fileData.url = await getDownloadURL(
                ref(storage, file.data().path)
              );
            }

            return fileData;
          });

        this.files = await Promise.all(filePromises);
        this.files = this.files.sort((a, b) =>
          a.created > b.created ? 1 : -1
        );
      }

      this.loading = false;
    },
    // POST
    async deleteFile(file) {
      file.delete = true;
      const batch = writeBatch(firestore);
      const fileDoc = doc(firestore, "media", file.id);

      if (file.type === "upload") {
        try {
          const fileRef = ref(storage, file.path);
          await deleteObject(fileRef);
        } catch (err) {
          if (err.code !== "storage/object-not-found") throw err;
        }
      }

      batch.delete(fileDoc);

      await batch.commit();
      this.files = this.files.filter((item) => item.id !== file.id);
      this.$root.toast.show({
        title: "Successfully deleted file!",
      });
    },
    /* Main */
    async setup() {
      if (this.relatedItem) {
        this.getFiles();
      } else {
        this.files = [];
        console.log("no related item");
      }
    },
    /* Misc */
    dragenter(e) {
      e.preventDefault();
      this.dragCounter++;
      if (this.dragCounter === 1) {
        this.isDragging = true;
      }
    },

    dragleave(e) {
      e.preventDefault();
      this.dragCounter--;
      if (this.dragCounter === 0) {
      }
      this.isDragging = false;
      this.dragCounter = 0;
    },

    validateFile(file) {
      if (!this.allowedFileTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} is not allowed`);
      }

      if (file.size > this.maxFileSize) {
        throw new Error(
          `File size exceeds ${this.maxFileSize / 1024 / 1024}MB limit`
        );
      }

      return true;
    },

    async handleDrop(e) {
      this.isDragging = false;
      this.dragCounter = 0;

      const files = Array.from(e.dataTransfer.files);

      for (const file of files) {
        try {
          this.validateFile(file);
          await this.uploadFile(file);
        } catch (error) {
          this.$root.toast.show({
            title: `Error uploading '${file.name}'`,
            subtitle: error.message,
            color: "error",
          });
        }
      }
    },

    async uploadFile(file) {
      try {
        this.uploadProgress = 0;

        // Create a unique path for the file
        const path = `accounts/${this.relatedItem.id}/${Date.now()}_${
          file.name
        }`;
        const storageRef = ref(storage, path);

        // Create upload task with progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Track upload progress
        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              this.uploadProgress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            },
            (error) => {
              reject(error);
            },
            () => {
              resolve();
            }
          );
        });

        // Create media document in Firestore
        const mediaRef = collection(firestore, "media");
        const mediaDoc = {
          relatedID: this.relatedItem.id,
          type: "upload",
          file: file.name,
          path: path,
          created: new Date(),
          size: file.size,
          mimeType: file.type,
          user: {
            name: {
              first: this.getUser.name.first,
              last: this.getUser.name.last,
            },
          },
        };

        await addDoc(mediaRef, mediaDoc);

        // Show success message
        this.$root.toast.show({
          title: `${file.name} uploaded successfully!`,
        });

        // Refresh the files list
        await this.getFiles();
      } catch (error) {
        console.error("Error uploading file:", error);
        throw error;
      } finally {
        this.uploadProgress = 0;
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
  computed: {
    ...mapGetters(["getAppName", "getUser"]),
  },
};
</script>

<style scoped>
.file-list {
  transition: all 0.2s ease;
}

.drag-active {
  background-color: rgba(0, 0, 0, 0.05);
  border: 2px dashed rgba(125, 121, 255, 0.673);
  border-radius: 8px;
  min-height: 125px;
}
</style>